/**
 * Get the vehicle fuel options from the current location.
 */

import type { RootState } from '/business/shared/state/store';
import { isEqual } from 'es-toolkit';
import { createSelectorCreator, lruMemoize } from 'reselect';

import getEditingItemKeySelector from '/business/shared/state/selectors/form/getEditingItemKey';
import type { QuestionTreeItem } from '/business/shared/functions/types';
import type { Step1SelectorProps } from '/business/shared/state/selectors/types';
import type { VehicleType } from '/index';
import { EMPTY_ARRAY } from '/business/shared/state/selectors/util';
import getVehicleTypesSelector from './getVehicleTypesSelector';

const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual);

const CARS = ['Cars', 'Car'];
const VANS = ['Vans'];
const HGV = ['Hgv'];
const BUSES_COACHES = ['Buses / coaches'];
const EV_FUEL = 'distance-ev';
const BUS_COACH_FUELS = ['distance-diesel', 'distance-hybrid'];
const BUS_COACH_SIZES = ['<7.5t', '7.5-12t', '>12t'];

type VehicleFuelOption = VehicleType['items'][number]['children']['items'][number];

const getAllowedVehicleTypes = (ownProps: Step1SelectorProps = {}): readonly string[] => {
  const { question } = ownProps;
  const questionParent = question?.parent as QuestionTreeItem | undefined;
  if (
    question?.id?.endsWith('cars') ||
    question?.id?.endsWith('car') ||
    question?.id?.endsWith('taxi') ||
    questionParent?.id?.endsWith('cars') ||
    questionParent?.id?.endsWith('car') ||
    questionParent?.id?.endsWith('taxi') ||
    question?.p
  ) {
    return CARS;
  }
  if (
    question?.id?.endsWith('vans') ||
    question?.id?.endsWith('van') ||
    questionParent?.id?.endsWith('cars') ||
    questionParent?.id?.endsWith('car') ||
    question?.p
  ) {
    return VANS;
  }
  if (
    question?.id?.endsWith('trucks') ||
    question?.id?.endsWith('truck') ||
    questionParent?.id?.endsWith('cars') ||
    questionParent?.id?.endsWith('car') ||
    question?.p
  ) {
    return HGV;
  }
  if (question?.id?.endsWith('buses-coaches')) {
    return BUSES_COACHES;
  }
  return EMPTY_ARRAY;
};

const allowedVehicleTypesSelector = (_state: RootState, ownProps: Step1SelectorProps = {}): readonly string[] =>
  getAllowedVehicleTypes(ownProps);

export default createDeepEqualSelector(
  [getVehicleTypesSelector, allowedVehicleTypesSelector, getEditingItemKeySelector],
  (vehicleTypes, allowedVehicleTypes, editingKeyValue) => {
    if (!vehicleTypes?.items) {
      return null;
    }
    let vehTypesMutable = allowedVehicleTypes;
    if (editingKeyValue) {
      vehTypesMutable = getAllowedVehicleTypes({ question: { id: editingKeyValue } });
    }

    const isBusCoach = vehTypesMutable.includes(BUSES_COACHES[0]);
    const excludesEv = isBusCoach || vehTypesMutable.includes(HGV[0]);

    return vehicleTypes?.items
      ?.filter(({ title }) => vehTypesMutable.indexOf(title) > -1)
      ?.reduce<VehicleFuelOption[]>((memo, { children }) => {
        const fuelOptions = excludesEv ? children.items.filter(({ title }) => title !== EV_FUEL) : children.items;
        const options = isBusCoach
          ? fuelOptions
              .filter(({ title }) => BUS_COACH_FUELS.includes(title))
              .sort((left, right) => BUS_COACH_FUELS.indexOf(left.title) - BUS_COACH_FUELS.indexOf(right.title))
              .map((option) => ({
                ...option,
                children: {
                  ...option.children,
                  items: option.children.items.filter(({ title }) => BUS_COACH_SIZES.includes(title)),
                },
              }))
          : fuelOptions;
        return [...options, ...memo];
      }, []);
  },
);
